// AP-CLN-04 (MED فاز 2): روند نتایج با محدوده مرجع و علامت دارو (US-MED-055؛ SCR-STAFF-702)، نامه و پاسخ ارجاع خارجی (US-MED-063)، ارجاع‌های ورودی
// (US-MED-064؛ SCR-STAFF-705) و فهرست ارجاع‌دهندگان (SCR-STAFF-706). نمودار SVG بدون کتابخانه نمودار (C-AGENT-1482).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type MedInboundReferralView,
  type MedReferrerKind,
  type MedReferrerView,
  type MedReferralUrgency,
  type MedResultSeriesResponse,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, LoadingState, toJalali, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { medKeys, num, saveBlob } from './common';

const WIDTH = 640;
const HEIGHT = 220;
const PAD = 36;

/** نمودار روند یک آزمایه (AC-MED-055.1): نقاط بر حسب زمان، نوار محدوده مرجع و خطوط عمودی صدور/ابطال نسخه. */
export function ResultTrendChart({ series }: { series: MedResultSeriesResponse }) {
  const { t } = useTranslation('med');
  const points = series.points.map((point) => ({ ...point, time: Date.parse(point.at), y: num(point.value) })).filter((point) => point.y !== null) as Array<
    MedResultSeriesResponse['points'][number] & { time: number; y: number }
  >;
  if (points.length === 0) {
    return <Typography variant="body2">{t('med:trend.no_numeric')}</Typography>;
  }
  const lows = points.map((point) => num(point.referenceLow)).filter((value): value is number => value !== null);
  const highs = points.map((point) => num(point.referenceHigh)).filter((value): value is number => value !== null);
  const values = [...points.map((point) => point.y), ...lows, ...highs];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min || 1;
  const times = [...points.map((point) => point.time), ...series.markers.map((marker) => Date.parse(marker.at))];
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const x = (time: number) => PAD + ((time - t0) / (t1 - t0 || 1)) * (WIDTH - 2 * PAD);
  const y = (value: number) => HEIGHT - PAD - ((value - min) / spread) * (HEIGHT - 2 * PAD);
  const low = lows.length ? Math.min(...lows) : null;
  const high = highs.length ? Math.max(...highs) : null;
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.time).toFixed(1)},${y(point.y).toFixed(1)}`).join(' ');
  const label = t('med:trend.chart_label', { name: series.name ?? series.analyteCode, count: points.length });
  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={label} style={{ width: '100%', maxWidth: WIDTH, direction: 'ltr' }} data-testid="med-trend-chart">
      <title>{label}</title>
      {low !== null && high !== null && <rect x={PAD} width={WIDTH - 2 * PAD} y={y(high)} height={Math.max(1, y(low) - y(high))} fill="currentColor" opacity={0.08} data-testid="med-trend-range" />}
      {series.markers.map((marker) => (
        <g key={`${marker.prescriptionId}-${marker.kind}`} data-testid="med-trend-marker">
          <line x1={x(Date.parse(marker.at))} x2={x(Date.parse(marker.at))} y1={PAD / 2} y2={HEIGHT - PAD} stroke="currentColor" strokeDasharray="4 3" opacity={0.5} />
          <title>{`${t(`med:trend.marker.${marker.kind}`, { defaultValue: marker.kind })}: ${marker.drugs.join(t('med:common.list_separator'))}`}</title>
        </g>
      ))}
      <path d={path} fill="none" stroke="currentColor" strokeWidth={2} />
      {points.map((point) => (
        <circle key={point.resultId} cx={x(point.time)} cy={y(point.y)} r={4} fill={point.flag && point.flag !== 'Normal' ? '#c62828' : 'currentColor'} data-testid="med-trend-point">
          <title>{`${point.y} ${point.unit ?? ''}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/** پنل روند در نتایج مراجعه‌کننده (US-MED-055). */
export function ResultTrendPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const [code, setCode] = useState('');
  const analytes = useQuery({
    queryKey: medKeys.analytes(tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/results/analytes', { params: { path: { patientId } } })),
  });
  const series = useQuery({
    queryKey: medKeys.series(tenantId, patientId, code),
    enabled: code !== '',
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/results/series', { params: { path: { patientId }, query: { analyteCode: code } } })),
  });
  if (analytes.isPending) {
    return <LoadingState rows={1} />;
  }
  if (analytes.error) {
    return <ErrorState error={analytes.error} onRetry={() => void analytes.refetch()} compact />;
  }
  return (
    <Stack spacing={1} data-testid="med-trend-panel">
      <TextField select size="small" label={t('med:trend.analyte')} value={code} onChange={(event) => setCode(event.target.value)} sx={{ maxWidth: 320 }} slotProps={{ htmlInput: { 'data-testid': 'med-trend-analyte' } }}>
        {(analytes.data?.items ?? []).map((item) => (
          <MenuItem key={item.code} value={item.code}>
            {item.name} ({item.count})
          </MenuItem>
        ))}
      </TextField>
      {series.data && (
        <>
          <ResultTrendChart series={series.data} />
          <ul style={{ margin: 0, paddingInlineStart: 20 }} aria-label={t('med:trend.points')}>
            {series.data.points.map((point) => (
              <li key={point.resultId}>
                <Typography variant="body2" component="span">
                  {toJalaliDateTime(point.at, { timeZone })}: <bdi dir="ltr">{num(point.value) ?? point.textValue} {point.unit}</bdi>
                </Typography>
              </li>
            ))}
            {series.data.markers.map((marker) => (
              <li key={`${marker.prescriptionId}-${marker.kind}`}>
                <Typography variant="body2" component="span" color="text.secondary">
                  {toJalaliDateTime(marker.at, { timeZone })}: {t(`med:trend.marker.${marker.kind}`, { defaultValue: marker.kind })} — {marker.drugs.join(t('med:common.list_separator'))}
                </Typography>
              </li>
            ))}
          </ul>
        </>
      )}
    </Stack>
  );
}

/** نامه، ارسال و پاسخ ارجاع خارجی (AC-MED-063.1). */
export function ExternalReferralDialog({ open, referralId, onClose }: { open: boolean; referralId: string; onClose: () => void }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const [summary, setSummary] = useState('');
  const [include, setInclude] = useState({ allergies: true, problems: true, medications: true });
  const [notice, setNotice] = useState<string | null>(null);
  const referral = useQuery({
    queryKey: medKeys.referral(tenantId, referralId),
    enabled: open,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referrals/{id}', { params: { path: { id: referralId } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['medorders', tenantId] });
  const issue = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referrals/{id}:issue-letter', {
          params: { path: { id: referralId } },
          body: { clinicalSummary: summary.trim() || null, includeAllergies: include.allergies, includeProblems: include.problems, includeMedications: include.medications },
        }),
      ),
    onSuccess: refresh,
  });
  const share = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/medical-orders/referrals/{id}:share-letter', { params: { path: { id: referralId } }, body: { channel: 'Portal' } })),
    onSuccess: () => setNotice(t('med:referral.letter_shared')),
  });
  const respond = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return unwrap(
        tenantApi.POST('/api/v1/medical-orders/referrals/{id}/external-response', {
          params: { path: { id: referralId } },
          body: form as never,
          bodySerializer: (value: unknown) => value as FormData,
        }),
      );
    },
    onSuccess: refresh,
  });
  const download = async () => {
    const response = await tenantApi.GET('/api/v1/medical-orders/referrals/{id}/letter', { params: { path: { id: referralId } }, parseAs: 'blob' });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${referral.data?.letterNumber ?? 'referral'}.pdf`);
    }
  };
  const data = referral.data;
  return (
    <FormDialog open={open} titleKey="med:referral.external_title" submitKey="med:common.close" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} maxWidth="md">
      {referral.isPending ? (
        <LoadingState rows={3} />
      ) : referral.error || !data ? (
        <ErrorState error={referral.error} onRetry={() => void referral.refetch()} compact />
      ) : (
        <Stack spacing={1.5} data-testid="med-external-referral">
          <Typography variant="body2">
            {t('med:referral.to')}: {data.externalName ?? '—'} {data.externalContact ? `· ${data.externalContact}` : ''} · {t(`med:referral_status.${data.status}`, { defaultValue: data.status })}
          </Typography>
          {data.letterDocumentId ? (
            <Alert severity="success" data-testid="med-referral-letter-issued">
              {t('med:referral.letter_issued', { number: data.letterNumber ?? '', at: data.letterIssuedAt ? toJalaliDateTime(data.letterIssuedAt, { timeZone }) : '' })}
            </Alert>
          ) : (
            <Stack spacing={1}>
              <TextField label={t('med:referral.clinical_summary')} value={summary} multiline minRows={3} onChange={(event) => setSummary(event.target.value)} slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'med-referral-summary' } }} />
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {(['allergies', 'problems', 'medications'] as const).map((key) => (
                  <FormControlLabel key={key} control={<Checkbox checked={include[key]} onChange={(event) => setInclude((current) => ({ ...current, [key]: event.target.checked }))} />} label={t(`med:referral.include_${key}`)} />
                ))}
              </Stack>
              {issue.error ? <ErrorState error={issue.error} compact /> : null}
              <Button variant="contained" onClick={() => issue.mutate()} disabled={issue.isPending} data-testid="med-referral-issue-letter">
                {t('med:referral.issue_letter')}
              </Button>
            </Stack>
          )}
          {data.letterDocumentId && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Button onClick={() => void download()} data-testid="med-referral-letter-download">
                {t('med:referral.letter_download')}
              </Button>
              <Button onClick={() => share.mutate()} disabled={share.isPending} data-testid="med-referral-letter-share">
                {t('med:referral.letter_share_portal')}
              </Button>
              {data.status === 'Sent' && (
                <Button component="label" variant="outlined" data-testid="med-referral-response-upload">
                  {t('med:referral.attach_response')}
                  <input hidden type="file" accept="application/pdf,image/*" onChange={(event) => event.target.files?.[0] && respond.mutate(event.target.files[0])} data-testid="med-referral-response-file" />
                </Button>
              )}
            </Stack>
          )}
          {notice && <Alert severity="info">{notice}</Alert>}
          {data.responseDocumentId && <Alert severity="success" data-testid="med-referral-response-attached">{t('med:referral.response_attached')}</Alert>}
          {share.error ? <ErrorState error={share.error} compact /> : null}
          {respond.error ? <ErrorState error={respond.error} compact /> : null}
        </Stack>
      )}
    </FormDialog>
  );
}

const KINDS: readonly MedReferrerKind[] = ['Physician', 'Facility'];
const URGENCIES: readonly MedReferralUrgency[] = ['Routine', 'Urgent'];

/** ثبت ارجاع ورودی (AC-MED-064.1). */
export function RegisterInboundDialog({ open, patientId, onClose }: { open: boolean; patientId: string; onClose: () => void }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const [referrerId, setReferrerId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<MedReferrerKind>('Physician');
  const [specialty, setSpecialty] = useState('');
  const [reason, setReason] = useState('');
  const [letterDate, setLetterDate] = useState('');
  const [urgency, setUrgency] = useState<MedReferralUrgency>('Routine');
  const [file, setFile] = useState<File | null>(null);
  const referrers = useQuery({ queryKey: medKeys.referrers(tenantId), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referrers')) });
  const valid = reason.trim() !== '' && (referrerId !== '' || name.trim() !== '');
  const save = useMutation({
    mutationFn: async () => {
      const created = await unwrap(
        tenantApi.POST('/api/v1/medical-orders/inbound-referrals', {
          body: {
            patientId,
            referrerId: referrerId || null,
            newReferrer: referrerId ? null : { name: name.trim(), kind, specialty: specialty.trim() || null, councilNumber: null, phone: null, address: null, isActive: null },
            reason: reason.trim(),
            letterDate: letterDate || null,
            urgency,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      );
      if (file) {
        const form = new FormData();
        form.append('file', file);
        await unwrap(
          tenantApi.POST('/api/v1/medical-orders/inbound-referrals/{id}/letter', {
            params: { path: { id: created.id } },
            body: form as never,
            bodySerializer: (value: unknown) => value as FormData,
            headers: { 'Idempotency-Key': crypto.randomUUID() },
          }),
        );
      }
      return created;
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['medorders', tenantId] });
      onClose();
    },
  });
  return (
    <FormDialog open={open} titleKey="med:inbound.register" submitKey="med:inbound.submit" onSubmit={() => (valid ? save.mutate() : undefined)} onClose={onClose} isDirty={reason.trim() !== ''} isSubmitting={save.isPending} submitError={save.error}>
      <TextField select label={t('med:inbound.referrer')} value={referrerId} onChange={(event) => setReferrerId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-inbound-referrer' } }}>
        <MenuItem value="">{t('med:inbound.new_referrer')}</MenuItem>
        {(referrers.data?.items ?? []).filter((item) => item.isActive).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name} · {t(`med:referrer_kind.${item.kind}`)}
          </MenuItem>
        ))}
      </TextField>
      {!referrerId && (
        <Stack direction="row" spacing={1}>
          <TextField fullWidth required label={t('med:inbound.referrer_name')} value={name} onChange={(event) => setName(event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'med-inbound-name' } }} />
          <TextField select label={t('med:inbound.referrer_kind')} value={kind} onChange={(event) => setKind(event.target.value as MedReferrerKind)} sx={{ minWidth: 140 }}>
            {KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`med:referrer_kind.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label={t('med:inbound.specialty')} value={specialty} onChange={(event) => setSpecialty(event.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
        </Stack>
      )}
      <TextField required label={t('med:inbound.reason')} value={reason} multiline minRows={2} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'med-inbound-reason' } }} />
      <Stack direction="row" spacing={1}>
        <TextField type="date" label={t('med:inbound.letter_date')} value={letterDate} onChange={(event) => setLetterDate(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField select label={t('med:referral.urgency')} value={urgency} onChange={(event) => setUrgency(event.target.value as MedReferralUrgency)} sx={{ minWidth: 140 }}>
          {URGENCIES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:priority.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Button component="label" variant="outlined">
        {file ? file.name : t('med:inbound.attach_letter')}
        <input hidden type="file" accept="application/pdf,image/*" onChange={(event) => setFile(event.target.files?.[0] ?? null)} data-testid="med-inbound-file" />
      </Button>
    </FormDialog>
  );
}

/** ارجاع‌های ورودی (مراجعه‌کننده یا همه) بدون دلیل بالینی در فهرست (SCR-STAFF-705). */
export function InboundReferralsPanel({ patientId }: { patientId: string | null }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const [registering, setRegistering] = useState(false);
  const canRegister = access.accessMode === 'Full' && access.can('med.referral.schedule');
  const list = useQuery({
    queryKey: medKeys.inbound(tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/inbound-referrals', { params: { query: patientId ? { patientId } : {} } })),
  });
  const columns: DataTableColumn<MedInboundReferralView>[] = [
    ...(patientId ? [] : [{ id: 'patient', headerKey: 'med:common.patient', cell: (row: MedInboundReferralView) => row.patientName ?? '—' }]),
    { id: 'referrer', headerKey: 'med:inbound.referrer', cell: (row) => `${row.referrerName} · ${t(`med:referrer_kind.${row.referrerKind}`)}` },
    { id: 'urgency', headerKey: 'med:referral.urgency', cell: (row) => t(`med:priority.${row.urgency}`) },
    { id: 'letter', headerKey: 'med:inbound.letter', cell: (row) => (row.letterDocumentId ? t('med:inbound.has_letter') : '—') },
    { id: 'letterDate', headerKey: 'med:inbound.letter_date', cell: (row) => (row.letterDate ? toJalali(row.letterDate) : '—') },
    { id: 'registered', headerKey: 'med:common.date', cell: (row) => toJalaliDateTime(row.registeredAt, { timeZone }) },
  ];
  return (
    <Stack spacing={1} data-testid="med-inbound-panel">
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
          {t('med:inbound.title')}
        </Typography>
        {canRegister && patientId && (
          <Button onClick={() => setRegistering(true)} data-testid="med-inbound-register">
            {t('med:inbound.register')}
          </Button>
        )}
      </Stack>
      <DataTable captionKey="med:inbound.title" columns={columns} rows={list.data?.items} getRowId={(row) => row.id} isLoading={list.isPending} error={list.error} onRetry={() => void list.refetch()} emptyTitleKey="med:inbound.empty" />
      {registering && patientId && <RegisterInboundDialog open patientId={patientId} onClose={() => setRegistering(false)} />}
    </Stack>
  );
}

/** فهرست ارجاع‌دهندگان و مقاصد بیرونی (SCR-STAFF-706؛ BR-MED-061) با سهم کانال. */
export function ReferrersSettingsPanel() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const [editing, setEditing] = useState<MedReferrerView | 'new' | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<MedReferrerKind>('Physician');
  const [specialty, setSpecialty] = useState('');
  const [phone, setPhone] = useState('');
  const [active, setActive] = useState(true);
  const full = access.accessMode === 'Full';
  const list = useQuery({ queryKey: medKeys.referrers(tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referrers', { params: { query: { includeInactive: true } } })) });
  const open = (row: MedReferrerView | 'new') => {
    setEditing(row);
    setName(row === 'new' ? '' : row.name);
    setKind(row === 'new' ? 'Physician' : row.kind);
    setSpecialty(row === 'new' ? '' : (row.specialty ?? ''));
    setPhone(row === 'new' ? '' : (row.phone ?? ''));
    setActive(row === 'new' ? true : row.isActive);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), kind, specialty: specialty.trim() || null, councilNumber: null, phone: phone.trim() || null, address: null, isActive: active };
      return editing === 'new' || editing === null
        ? unwrap(tenantApi.POST('/api/v1/medical-orders/settings/referrers', { body, headers: { 'Idempotency-Key': key.current() } }))
        : unwrap(tenantApi.PUT('/api/v1/medical-orders/settings/referrers/{id}', { params: { path: { id: editing.id } }, body }));
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: medKeys.referrers(tenantId) });
    },
  });
  const columns: DataTableColumn<MedReferrerView>[] = [
    { id: 'name', headerKey: 'med:inbound.referrer_name', cell: (row) => row.name },
    { id: 'kind', headerKey: 'med:inbound.referrer_kind', cell: (row) => t(`med:referrer_kind.${row.kind}`) },
    { id: 'specialty', headerKey: 'med:inbound.specialty', cell: (row) => row.specialty ?? '—' },
    { id: 'count', headerKey: 'med:inbound.count', cell: (row) => row.inboundCount },
    { id: 'active', headerKey: 'med:common.status', cell: (row) => (row.isActive ? t('med:common.active') : t('med:common.inactive')) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        full ? (
          <Button size="small" onClick={() => open(row)} data-testid="med-referrer-edit">
            {t('med:common.edit')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={1} data-testid="med-referrers-panel">
      {full && (
        <Button sx={{ alignSelf: 'flex-start' }} onClick={() => open('new')} data-testid="med-referrer-add">
          {t('med:inbound.add_referrer')}
        </Button>
      )}
      <DataTable captionKey="med:inbound.referrers" columns={columns} rows={list.data?.items} getRowId={(row) => row.id} isLoading={list.isPending} error={list.error} onRetry={() => void list.refetch()} emptyTitleKey="med:inbound.no_referrers" />
      {editing && (
        <FormDialog open titleKey="med:inbound.referrer" submitKey="med:common.save" onSubmit={() => (name.trim() ? save.mutate() : undefined)} onClose={() => setEditing(null)} isDirty={name.trim() !== ''} isSubmitting={save.isPending} submitError={save.error}>
          <TextField required label={t('med:inbound.referrer_name')} value={name} onChange={(event) => setName(event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'med-referrer-name' } }} />
          <TextField select label={t('med:inbound.referrer_kind')} value={kind} onChange={(event) => setKind(event.target.value as MedReferrerKind)}>
            {KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`med:referrer_kind.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('med:inbound.specialty')} value={specialty} onChange={(event) => setSpecialty(event.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
          <TextField label={t('med:inbound.phone')} value={phone} onChange={(event) => setPhone(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', maxLength: 32 } }} />
          <FormControlLabel control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />} label={t('med:common.active')} />
        </FormDialog>
      )}
    </Stack>
  );
}
